import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import { organizationTeamService } from '../services/organization/OrganizationTeamService';
import {
  AddOrganizationMemberByUserPayload,
  AddOrganizationMemberPayload,
  OrganizationRolePayload,
  TeamScope,
  teamScopeKey,
} from '../types/organizationTeam';

const teamKeys = {
  root: () => [DashboardQueryKey.ORGANIZATION_TEAM] as const,
  scope: (s: TeamScope) => [DashboardQueryKey.ORGANIZATION_TEAM, teamScopeKey(s)] as const,
  summary: (orgId: string) => [DashboardQueryKey.ORGANIZATION_TEAM, orgId, 'summary'] as const,
  permissions: (s: TeamScope) => [...teamKeys.scope(s), 'permissions'] as const,
  roles: (s: TeamScope) => [...teamKeys.scope(s), 'roles'] as const,
  members: (s: TeamScope) => [...teamKeys.scope(s), 'members'] as const,
};

/**
 * Roles and members change each other's counts, and membership shows on User
 * Details, so every write refreshes every team (both scopes can show the same
 * organization) and the user details caches.
 *
 * Returned as a promise so `onSuccess` awaits the refetch: the role selects
 * are controlled by server data, and settling before it lands would flash the
 * old role.
 */
function useInvalidateTeam({ alsoUsers = false } = {}) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: teamKeys.root() }),
      // Root of dashboardQueryKeys.userDetails(id) is [USERS, 'detail', id].
      alsoUsers ? qc.invalidateQueries({ queryKey: [DashboardQueryKey.USERS, 'detail'] }) : undefined,
    ]);
}

export function useOrganizationTeamSummaryQuery(orgId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: teamKeys.summary(orgId ?? ''),
    queryFn: () => organizationTeamService.summary(orgId as string),
    enabled: !!orgId && enabled,
  });
}

export function useOrganizationPermissionsQuery(scope: TeamScope, enabled = true) {
  return useQuery({
    queryKey: teamKeys.permissions(scope),
    queryFn: () => organizationTeamService.listPermissions(scope),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrganizationRolesQuery(scope: TeamScope, enabled = true) {
  return useQuery({
    queryKey: teamKeys.roles(scope),
    queryFn: () => organizationTeamService.listRoles(scope),
    enabled,
  });
}

export function useOrganizationMembersQuery(scope: TeamScope, enabled = true) {
  return useQuery({
    queryKey: teamKeys.members(scope),
    queryFn: () => organizationTeamService.listMembers(scope),
    enabled,
  });
}

export function useSaveOrganizationRoleMutation(scope: TeamScope) {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: OrganizationRolePayload }) =>
      id
        ? organizationTeamService.updateRole(scope, id, payload)
        : organizationTeamService.createRole(scope, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteOrganizationRoleMutation(scope: TeamScope) {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: (id: string) => organizationTeamService.deleteRole(scope, id),
    onSuccess: invalidate,
  });
}

export function useAddOrganizationMemberMutation(scope: TeamScope) {
  const invalidate = useInvalidateTeam({ alsoUsers: true });
  return useMutation({
    mutationFn: (payload: AddOrganizationMemberPayload | AddOrganizationMemberByUserPayload) =>
      organizationTeamService.addMember(scope, payload),
    onSuccess: invalidate,
  });
}

export function useSetOrganizationMemberRoleMutation(scope: TeamScope) {
  const invalidate = useInvalidateTeam({ alsoUsers: true });
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      organizationTeamService.setMemberRole(scope, userId, roleId),
    onSuccess: invalidate,
  });
}

export function useRemoveOrganizationMemberMutation(scope: TeamScope) {
  const invalidate = useInvalidateTeam({ alsoUsers: true });
  return useMutation({
    mutationFn: (userId: string) => organizationTeamService.removeMember(scope, userId),
    onSuccess: invalidate,
  });
}
